import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useFeatures } from "@/lib/features";
import { api, brl } from "@/lib/api";
import { Money, HideValuesToggle } from "@/components/HideValues";
import { ClipboardList, Users, History, LineChart, Settings, Plus, Store, FileSpreadsheet, Wallet, Bell, Filter, Star, Boxes, PackagePlus, Truck, Landmark, TrendingUp, Receipt, ShoppingCart, Columns3, CalendarDays, Hourglass, ChevronRight, Trophy, Wrench, AlertTriangle, Handshake, Contact, Calculator } from "lucide-react";
import { useSatisfaction } from "@/pages/OwnerSatisfaction";
import { TopCards, GoalBar, usePanel } from "@/components/PanelCards";
import { AskAssistant } from "@/components/AskAssistant";
import { ClientSourcesSection, GoalsSection } from "@/components/PanelExtras";
import { ChecklistPendingCard } from "@/components/ChecklistPendingCard";
import { DueTodayCard } from "@/components/DueTodayCard";
import { BirthdaysHubCard } from "@/components/BirthdaysHubCard";
import { PeriodPicker, PERIOD_RANGES } from "@/components/PeriodPicker";
import { FinanceOverview } from "@/components/FinanceOverview";

/**
 * Lista central de atalhos. Adicionar um novo quadradinho é só adicionar um item aqui.
 */
export const HUB_SHORTCUTS = [
  { group: "Operação", color: "#38bdf8", items: [
    { to: "/owner/patio", label: "Pátio", icon: Columns3, testid: "hub-patio" },
    { to: "/owner/agendamentos", label: "Agendamentos", icon: CalendarDays, testid: "hub-agendamentos" },
    { to: "/owner/ordens", label: "Ordens de serviço", icon: ClipboardList, testid: "hub-ordens" },
    { to: "/owner/historico", label: "Histórico", icon: History, testid: "hub-historico" },
  ]},
  { group: "Cadastro", color: "#a78bfa", items: [
    { to: "/owner/customers", label: "Clientes", icon: Users, testid: "hub-clientes" },
    { to: "/owner/customers?io=1", label: "Importar / Exportar", icon: FileSpreadsheet, testid: "hub-import-export" },
    { to: "/owner/mao-de-obra", label: "Mão de obra", icon: Wrench, testid: "hub-mao-de-obra" },
    { to: "/owner/funcionarios", label: "Funcionários (RH)", icon: Contact, testid: "hub-funcionarios" },
  ]},
  { group: "CRM", color: "#f472b6", items: [
    { to: "/owner/lembretes", label: "Lembretes", icon: Bell, testid: "hub-lembretes" },
    { to: "/owner/funil", label: "Funil de orçamentos", icon: Filter, testid: "hub-funil" },
    { to: "/owner/pos-venda", label: "Pós-venda", icon: Star, testid: "hub-pos-venda" },
    { to: "/owner/clube", label: "Clube de pontos", icon: Trophy, testid: "hub-clube" },
    { to: "/owner/satisfacao", label: "Satisfação", icon: Star, testid: "hub-satisfacao" },
  ]},
  { group: "Estoque e compras", color: "#fb923c", items: [
    { to: "/owner/estoque", label: "Estoque", icon: Boxes, testid: "hub-estoque" },
    { to: "/owner/compras", label: "Compras", icon: PackagePlus, testid: "hub-compras" },
    { to: "/owner/sugestao-compra", label: "Sugestão de compra", icon: AlertTriangle, testid: "hub-sugestao" },
    { to: "/owner/fornecedores", label: "Fornecedores", icon: Truck, testid: "hub-fornecedores" },
  ]},
  { group: "Financeiro", color: "#4ade80", items: [
    { to: "/owner/financeiro", label: "Financeiro", icon: Landmark, testid: "hub-financeiro" },
    { to: "/owner/dre", label: "DRE e balancete", icon: TrendingUp, testid: "hub-dre" },
    { to: "/owner/rentabilidade", label: "Rentabilidade", icon: TrendingUp, testid: "hub-rentabilidade" },
    { to: "/owner/terceirizados", label: "Terceirizados", icon: Handshake, testid: "hub-terceirizados" },
    { to: "/owner/calculadoras", label: "Calculadoras", icon: Calculator, testid: "hub-calculadoras" },
    { to: "/owner/notas", label: "Notas fiscais", icon: Receipt, testid: "hub-notas" },
  ]},
  { group: "Análise", color: "#facc15", items: [
    { to: "/owner/dashboard", label: "Dashboard", icon: LineChart, testid: "hub-dashboard" },
    { to: "/owner/equipe", label: "Equipe", icon: Users, testid: "hub-equipe" },
    { to: "/owner/relatorios/comissoes", label: "Comissões", icon: Wallet, testid: "hub-comissoes" },
    { to: "/owner/relatorios/veiculos-funcionario", label: "Veículos por Funcionário", icon: ClipboardList, testid: "hub-veiculos-funcionario" },
    { to: "/owner/pneus", label: "Venda de pneus", icon: TrendingUp, testid: "hub-pneus" },
    { to: "/owner/margem-baixa", label: "Margem baixa", icon: AlertTriangle, testid: "hub-margem-baixa" },
    { to: "/owner/dashboard#pagamentos", label: "Faturamento", icon: Wallet, testid: "hub-faturamento" },
  ]},
  { group: "Configuração", color: "#a1a1aa", items: [
    { to: "/owner/settings", label: "Configurações", icon: Settings, testid: "hub-config" },
  ]},
];

const SUMMARY = [
  { k: "yard", label: "Carros no pátio", icon: Columns3, to: "/owner/patio", testid: "hub-sum-yard" },
  { k: "appointments_today", label: "Agendamentos hoje", icon: CalendarDays, to: "/owner/agendamentos", testid: "hub-sum-appts" },
  { k: "stale_budgets", label: "Orçamentos parados 3+ dias", icon: Hourglass, to: "/owner/funil", testid: "hub-sum-stale", warn: true },
  { k: "received_today", label: "Recebido hoje", icon: Wallet, to: "/owner/financeiro", testid: "hub-sum-received", money: true },
];

function SummaryCard({ it, value }) {
  const Icon = it.icon;
  const hot = it.warn && value > 0;
  return (
    <Link to={it.to} data-testid={it.testid} className={`group h-full rounded-2xl lg:rounded-xl border p-4 sm:p-5 lg:p-3 bg-zinc-950/70 hover:bg-zinc-900 transition-colors flex flex-col gap-3 lg:gap-1.5 ${hot ? "border-red-800" : "border-card2-border"}`}>
      <div className="flex items-center justify-between">
        <Icon className={`h-5 w-5 ${hot ? "text-red-400" : "text-zinc-500"}`} />
        <ChevronRight className="h-4 w-4 text-zinc-600 group-hover:translate-x-0.5 transition-transform" />
      </div>
      <div data-testid={`${it.testid}-value`} className={`font-display font-black leading-none ${it.money ? "text-2xl sm:text-3xl lg:text-xl" : "text-4xl lg:text-2xl"} ${hot ? "text-red-400" : "text-zinc-50"}`}>
        {value == null ? "—" : it.money ? <Money value={value} /> : value}
      </div>
      <div className="text-xs text-zinc-400 leading-tight">{it.label}</div>
    </Link>
  );
}

function HeroAction({ to, label, sub, icon: Icon, testid, disabled }) {
  const cls = "relative overflow-hidden rounded-2xl lg:rounded-xl p-5 sm:p-6 lg:p-3 flex items-center gap-4 lg:gap-3 min-h-[96px] lg:min-h-[60px] transition-transform active:scale-[0.98]";
  const style = { background: "var(--c-brand)", color: "var(--c-brand-fg)", boxShadow: "0 14px 40px -16px rgba(var(--c-brand-rgb)/0.7)" };
  const body = (
    <>
      <span className="h-14 w-14 lg:h-9 lg:w-9 rounded-2xl lg:rounded-lg flex items-center justify-center bg-black/15 shrink-0"><Icon className="h-7 w-7 lg:h-5 lg:w-5" /></span>
      <span className="min-w-0"><span className="block font-display text-2xl sm:text-3xl lg:text-lg font-black uppercase leading-none">{label}</span><span className="block text-sm opacity-80 mt-1">{sub}</span></span>
      <ChevronRight className="h-6 w-6 ml-auto opacity-70" />
    </>
  );
  if (disabled) return <div data-testid={testid} aria-disabled="true" title="Escolha uma unidade específica" className={`${cls} opacity-50 cursor-not-allowed`} style={style}>{body}</div>;
  return <Link to={to} data-testid={testid} className={`${cls} hover:brightness-110`} style={style}>{body}</Link>;
}

function Tile({ to, label, icon: Icon, testid, color }) {
  return (
    <Link to={to} data-testid={testid} className="group h-full rounded-2xl border border-card2-border bg-zinc-950/60 hover:bg-zinc-900 hover:border-zinc-700 transition-colors flex items-center gap-3 p-4 lg:p-2.5 min-h-[76px] lg:min-h-[52px] lg:rounded-xl">
      <span className="h-11 w-11 lg:h-8 lg:w-8 rounded-xl lg:rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}22`, color }}><Icon className="h-5 w-5" /></span>
      <span className="text-sm font-bold text-zinc-100 leading-tight">{label}</span>
    </Link>
  );
}

export default function OwnerHub() {
  const { user } = useAuth();
  const feats = useFeatures();
  const [sum, setSum] = useState({});
  const [lowMargin, setLowMargin] = useState(null);
  const scopeAll = user?.shop_id === "all";
  const shopName = useMemo(() => (scopeAll ? "Todas as unidades" : user?.shop?.name || "Oficina"), [user, scopeAll]);
  useEffect(() => { api.get("/hub/today").then(({ data }) => setSum(data)).catch(() => setSum({})); }, [user?.shop_id]);
  useEffect(() => {
    if (!feats.enabled("relatorios.margem_baixa")) { setLowMargin(null); return; }
    const d = new Date();
    const from = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
    const to = d.toISOString().slice(0, 10);
    api.get("/erp/low-margin", { params: { from, to } }).then(({ data }) => setLowMargin(data)).catch(() => setLowMargin(null));
  }, [user?.shop_id, feats]);
  const [sat] = useSatisfaction({ shop: user?.shop_id });
  const [preset, setPreset] = useState("today");
  const [[from, to], setRange] = useState(() => PERIOD_RANGES[0].range());
  const [panel, reloadPanel] = usePanel(from, to);
  return (
    <div className="space-y-8 animate-fade-up" data-testid="owner-hub">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
        <div className="flex items-center gap-2 text-xs font-mono-caps text-zinc-500"><Store className="h-4 w-4" /> {shopName}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-zinc-100 mt-1">Olá, {user?.name?.split(" ")[0] || "Dono"}</h1>
        {scopeAll && <p data-testid="hub-scope-hint" className="text-xs text-amber-300 mt-2">Você está vendo <strong>todas as unidades</strong>. Para criar ordens, vendas ou clientes, escolha uma unidade no cabeçalho.</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {feats.enabled("relatorios.painel") && (
            <Link to="/owner/painel" data-testid="hub-open-panel" className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-green-brand/60 bg-green-brand/10 text-sm font-bold text-green-neon hover:bg-green-brand/20 transition-colors">
              <LineChart className="h-4 w-4" /> Ver painel completo
            </Link>
          )}
          <AskAssistant /><HideValuesToggle />
        </div>
      </div>
      {sat?.low_pending > 0 && (
        <Link to="/owner/satisfacao" data-testid="hub-low-ratings" className="flex items-center gap-3 rounded-2xl border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-4 text-sm text-red-900 dark:text-red-200">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" /> {sat.low_pending} cliente(s) deram nota baixa (1 a 3). Toque para ver e resolver.
        </Link>
      )}
      {lowMargin?.count > 0 && (
        <Link to="/owner/margem-baixa" data-testid="hub-low-margin" className="flex items-center gap-3 rounded-2xl border border-amber-400 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/30 p-4 text-sm text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors">
          <AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-300 shrink-0" />
          <span className="flex-1">
            <strong data-testid="hub-low-margin-count" className="font-display font-black text-amber-900 dark:text-amber-200">{lowMargin.count}</strong> {lowMargin.count === 1 ? "OS/venda abaixo da margem" : "OS/vendas abaixo da margem"} neste mês (mínimo {lowMargin.min_margin_pct}%). Toque para ver.
          </span>
          <ChevronRight className="h-4 w-4 text-amber-700 dark:text-amber-300" />
        </Link>
      )}
      <ChecklistPendingCard />
      <DueTodayCard />
      <BirthdaysHubCard />
      <ClientSourcesSection scopeKey={user?.shop_id} />
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="font-mono-caps text-xs text-zinc-500 uppercase tracking-widest">Resumo do dia</h2>
          <HideValuesToggle />
        </div>
        <PeriodPicker preset={preset} setPreset={setPreset} from={from} to={to} setRange={setRange} />
        <TopCards d={panel} showValues singleDay={from === to} />
        <GoalBar d={panel} onSaved={reloadPanel} />
      </section>
      <GoalsSection shopGoal={panel?.goal} scopeKey={user?.shop_id} />
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <HeroAction to="/owner/nova-ordem" label="Nova ordem" sub="Abrir OS para um veículo" icon={Plus} testid="hub-nova-ordem" disabled={scopeAll} />
        <HeroAction to="/owner/balcao" label="Venda balcão" sub="Peças e serviços sem OS" icon={ShoppingCart} testid="hub-balcao" disabled={scopeAll} />
      </section>
      <FinanceOverview from={from} to={to} scopeKey={user?.shop_id} />
      {HUB_SHORTCUTS.map((grp) => (
        <section key={grp.group}>
          <h2 className="flex items-center gap-2 font-mono-caps text-xs text-zinc-400 mb-3 uppercase tracking-widest"><span className="h-2 w-2 rounded-full" style={{ background: grp.color }} /> {grp.group}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{grp.items.map((it) => <Tile key={it.testid} {...it} color={grp.color} />)}</div>
        </section>
      ))}
    </div>
  );
}
